// Villager trade resolution (VIL, M14.1). PURE (ENG-1).
//
// Pure math over an inventory: does the player hold an offer's inputs, and if so
// consume them and hand back the sell stack. Per-offer use counts + villager XP
// live in the caller (a single-player client keeps them per-villager); this file
// only moves items. The offer's `maxUses` is enforced here via a `used` count.
import { type ItemStack, type Inventory, makeStack, emptyStack, isEmpty } from './inventory';
import { insert } from './transaction';
import type { TradeOffer } from '../registry/schemas';

export interface TradeItems {
  idOf: ReadonlyMap<string, number>;
}

/** An offer is available while it hasn't hit its use cap (VIL restock is manual). */
export function offerAvailable(offer: TradeOffer, used: number): boolean {
  return used < offer.maxUses;
}

/** Whether `inv` holds every input stack of `offer`. */
export function canAfford(items: TradeItems, inv: Inventory, offer: TradeOffer): boolean {
  for (const b of offer.buy) {
    const id = items.idOf.get(b.item);
    if (id === undefined || inv.countOf(id) < b.count) return false;
  }
  return true;
}

/** Remove `n` of item id from the inventory, front to back. */
function removeCount(inv: Inventory, id: number, n: number): void {
  let left = n;
  for (let i = 0; i < inv.size && left > 0; i++) {
    const s = inv.get(i);
    if (s.item !== id || s.count <= 0) continue;
    const take = Math.min(left, s.count);
    left -= take;
    inv.set(i, s.count - take <= 0 ? emptyStack() : { ...s, count: s.count - take });
  }
}

export interface TradeResult {
  ok: boolean;
  /** Sell items that didn't fit the inventory (caller drops them as entities). */
  leftover: ItemStack;
}

/** Execute one trade if affordable + available: consume inputs, deposit the sell
 *  stack into `range`. Returns ok=false (no mutation) when it can't be made. */
export function applyTrade(
  items: TradeItems, inv: Inventory, range: readonly number[], offer: TradeOffer, used: number,
): TradeResult {
  if (!offerAvailable(offer, used) || !canAfford(items, inv, offer)) return { ok: false, leftover: emptyStack() };
  const sid = items.idOf.get(offer.sell.item);
  if (sid === undefined) return { ok: false, leftover: emptyStack() };
  for (const b of offer.buy) removeCount(inv, items.idOf.get(b.item)!, b.count);
  const leftover = insert(inv, makeStack(sid, offer.sell.count), range);
  return { ok: true, leftover: isEmpty(leftover) ? emptyStack() : leftover };
}
